import { getLocale } from 'next-intl/server'
import { toBackendLocale, getLocalizedValue } from './utils'
import { fetchMetaData, fetchPages, fetchCategories } from './api'
import type { RouteParams } from '@/types/meta'
import { NextResponse } from 'next/server'
import config from '@/config'

/**
 * Helper to create a NextResponse with rewrite and set common headers
 *
 * @param url - The URL to rewrite to
 * @param pathWithoutLocale - The pathname without locale
 * @param currentLocale - The current locale
 * @param status - Optional status code for the rewrite
 * @returns The NextResponse object
 * @example const response = createRewriteResponse(new URL('/products/engine'), '/urunler/engine', 'tr-TR')
 */
export const createRewriteResponse = (
  url: URL,
  pathWithoutLocale: string,
  currentLocale: string,
  status?: number
): NextResponse => {
  const response = NextResponse.rewrite(url, status ? { status } : undefined)
  response.headers.set('x-pathname', pathWithoutLocale)
  response.headers.set('x-locale', currentLocale)
  return response
}

/**
 * Helper to create a NextResponse.next() with common headers
 *
 * @param pathWithoutLocale - The pathname without locale
 * @param currentLocale - The current locale
 * @returns The NextResponse object
 * @example const response = createNextResponse('/products/engine', 'tr-TR')
 */
export const createNextResponse = (pathWithoutLocale: string, currentLocale: string): NextResponse => {
  const response = NextResponse.next()
  response.headers.set('x-pathname', pathWithoutLocale)
  response.headers.set('x-locale', currentLocale)
  return response
}

/**
 * Server-side helper to get locale from Next.js (for use in Server Components).
 *
 * @returns {{ locale: string, backendLocale: string }} The URL locale and backend locale.
 */
export const getAppLocale = async (): Promise<{ locale: string; backendLocale: string }> => {
  const locale = await getLocale()
  const backendLocale = toBackendLocale(locale)

  return { locale, backendLocale }
}

// ============================================================================
// METADATA UTILITIES
// ============================================================================

/**
 * Fetches locale data from metadata API (no caching).
 *
 * @returns An object containing defaultLocale, availableLocales, and routeParams.
 */
export const getLocaleData = async (): Promise<{
  defaultLocale: string
  availableLocales: string[]
  routeParams: RouteParams
  backendAvailable: boolean
}> => {
  return Promise.resolve(fetchMetaData())
    .then(metadata => ({
      defaultLocale: metadata.generalSettings.appDefaultLocale.toLowerCase(),
      availableLocales: metadata.locales
        .filter((l: any) => l.active)
        .map((l: any) => `${l.code}-${l.countryCode}`.toLowerCase()),
      routeParams: metadata.routeParams || {},
      backendAvailable: true
    }))
    .catch((err: unknown) => {
      console.error(`[getLocaleData] Backend unreachable — URL: ${config.API_BASE_URL} — Error: ${String(err)}`)
      return {
        defaultLocale: 'en-us',
        availableLocales: ['en-us', 'tr-tr'],
        routeParams: {},
        backendAvailable: false
      }
    })
}

/**
 * Get metadata with localized content for the current locale
 * Returns metadata along with commonly used derived values
 */
export const getLocalizedMetaData = async () => {
  const { backendLocale } = await getAppLocale()

  try {
    const metaData = await fetchMetaData()

    const metaFirstLocale =
      Object.keys(metaData.contentSettings.title).length > 0 ? Object.keys(metaData.contentSettings.title)[0] : null

    const appTitle =
      metaData.contentSettings.title[backendLocale] ||
      (metaFirstLocale ? metaData.contentSettings.title[metaFirstLocale] : '')

    const appDescription =
      metaData.contentSettings.description[backendLocale] ||
      (metaFirstLocale ? metaData.contentSettings.description[metaFirstLocale] : '')

    const appKeywords =
      metaData.contentSettings.keywords[backendLocale] ||
      (metaFirstLocale ? metaData.contentSettings.keywords[metaFirstLocale] : '')

    const activeLocales = metaData.locales.filter(l => l.active).map(l => l.fullCode.toLowerCase())
    const defaultLocale = metaData.generalSettings.appDefaultLocale || 'en-US'

    const socialMediaLinks = []
    if (metaData.generalSettings.facebook) {
      socialMediaLinks.push(metaData.generalSettings.facebook)
    }
    if (metaData.generalSettings.instagram) {
      socialMediaLinks.push(metaData.generalSettings.instagram)
    }
    if (metaData.generalSettings.linkedin) {
      socialMediaLinks.push(metaData.generalSettings.linkedin)
    }
    if (metaData.generalSettings.youtube) {
      socialMediaLinks.push(metaData.generalSettings.youtube)
    }
    if (metaData.generalSettings.twitter) {
      socialMediaLinks.push(metaData.generalSettings.twitter)
    }

    return {
      metaData,
      activeLocales,
      defaultLocale,
      backendLocale,
      metaFirstLocale,
      appTitle,
      appDescription,
      appKeywords,
      generalSettings: metaData.generalSettings,
      socialMediaLinks
    }
  } catch (err: unknown) {
    console.error(`[getLocalizedMetaData] Backend unreachable — URL: ${config.API_BASE_URL} — Error: ${String(err)}`)
    // Return fallback metadata structure when the backend is unavailable
    return {
      metaData: {
        generalSettings: {
          appDefaultLocale: 'en-US',
          address: '',
          phone: '',
          email: '',
          fax: '',
          latitude: 0,
          longitude: 0
        },
        locales: [
          { code: 'en', countryCode: 'US', fullCode: 'en-US', active: true },
          { code: 'tr', countryCode: 'TR', fullCode: 'tr-TR', active: true }
        ],
        contentSettings: {
          title: { 'en-US': 'Synchtria', 'tr-TR': 'Synchtria' },
          description: {},
          keywords: {}
        },
        routeParams: {},
        i18nMessages: []
      } as any,
      backendLocale,
      metaFirstLocale: 'en-US',
      appTitle: 'Synchtria',
      appDescription: '',
      appKeywords: '',
      generalSettings: null,
      activeLocales: ['en-us', 'tr-tr'],
      defaultLocale: 'en-US',
      socialMediaLinks: []
    }
  }
}

/**
 * Helper type for the return value of getLocalizedMetaData
 */
export type LocalizedMetaData = Awaited<ReturnType<typeof getLocalizedMetaData>>

/**
 * Server-side redirect that handles dynamic locales from backend
 *
 * @param path - The localized path to redirect
 */
export const redirectWithLocale = async (path: string): Promise<never> => {
  const { redirect } = await import('next/navigation')
  const locale = await getLocale()
  return redirect(`/${locale}${path}`) as never
}

// ============================================================================
// ROUTE TRANSLATION UTILITIES
// ============================================================================

/**
 * Get route params from API (no caching)
 */
export const getRouteParams = async (): Promise<RouteParams> => {
  try {
    const metadata = await fetchMetaData()
    return metadata.routeParams || {}
  } catch {
    return {}
  }
}

/**
 * Get translated route path from API route params
 *
 * @param routePath - The route path (e.g., 'corporate', 'photo-galleries', 'videos')
 * @param locale - The backend locale (e.g., 'en-US', 'tr-TR')
 * @returns The translated route segment, or the routePath itself as fallback
 */
export const getTranslatedRoute = async (routePath: string, locale: string): Promise<string> => {
  const routeParams = await getRouteParams()

  // If a route param exists for this path and locale, return the translation
  if (routeParams[routePath]?.[locale]) {
    return routeParams[routePath][locale]
  }

  // Fallback: return the route path itself (default folder name)
  return routePath
}

/**
 * Build translated route path with parameters (SSR version)
 *
 * @param locale - The backend locale (e.g., 'en-US', 'tr-TR')
 * @param path - Route path segments separated by / (e.g., 'products', 'corporate/videos')
 * @param params - Additional path parameters to append (non-translated)
 * @returns Complete translated route path starting with /
 * @example
 * await buildTranslatedRoute('tr-TR', 'products', 'engine') // => /urunler/engine
 * await buildTranslatedRoute('tr-TR', 'corporate/videos') // => /kurumsal/videolar
 * await buildTranslatedRoute('tr-TR', 'corporate/videos', 'video-1') // => /kurumsal/videolar/video-1
 */
export const buildTranslatedRoute = async (locale: string, path: string, ...params: string[]): Promise<string> => {
  const routeParams = await getRouteParams()

  // Split the path into segments
  const segments = path.split('/').filter(Boolean)

  // Translate each segment
  const translatedSegments = segments.map(segment => {
    // If a route param exists for this segment and locale, return translation
    if (routeParams[segment]?.[locale]) {
      return routeParams[segment][locale]
    }
    // Fallback: return the segment itself (default folder name)
    return segment
  })

  // Combine translated segments with additional params
  const allSegments = [...translatedSegments, ...params.filter(Boolean)]

  // Return a complete path starting with /
  return '/' + allSegments.join('/')
}

// ============================================================================
// MENU UTILITIES
// ============================================================================

/**
 * Get localized menu items with dynamic corporate pages from API
 * This function should be called in server components for SSR
 */
export const getMenuItems = async (backendLocale: string) => {
  // Fetch data with error handling
  const [pages, categories] = await Promise.all([
    fetchPages().catch(() => []),
    fetchCategories({ active: true }).catch(() => [])
  ])

  // Get all corporate pages from API
  const corporatePagesData = pages.filter(page => {
    const slug = getLocalizedValue(page, 'slug', backendLocale)
    return slug && slug !== '-'
  })

  const corporatePages = await Promise.all(
    corporatePagesData.map(async page => {
      const title = getLocalizedValue(page, 'title', backendLocale)
      const slug = getLocalizedValue(page, 'slug', backendLocale)
      return {
        label: title,
        href: await buildTranslatedRoute(backendLocale, 'corporate', slug)
      }
    })
  )

  // Build product submenu from API categories (filter active categories)
  const productsCategoriesData = categories.filter(category => {
    const slug = getLocalizedValue(category, 'slug', backendLocale)
    return category.active && slug && slug !== '-'
  })

  const productsSubmenu = await Promise.all(
    productsCategoriesData.map(async category => {
      const name = getLocalizedValue(category, 'name', backendLocale)
      const slug = getLocalizedValue(category, 'slug', backendLocale)
      return {
        label: name,
        href: await buildTranslatedRoute(backendLocale, 'products', slug),
        picture: category.picture
      }
    })
  )

  // Build menu items with translated routes
  const [
    corporateHref,
    cataloguesHref,
    certificatesHref,
    photoGalleriesHref,
    videosHref,
    productsHref,
    productionHref,
    blogHref,
    eventsHref,
    careerHref,
    contactHref
  ] = await Promise.all([
    buildTranslatedRoute(backendLocale, 'corporate'),
    buildTranslatedRoute(backendLocale, 'corporate/catalogues'),
    buildTranslatedRoute(backendLocale, 'corporate/certificates'),
    buildTranslatedRoute(backendLocale, 'corporate/photo-galleries'),
    buildTranslatedRoute(backendLocale, 'corporate/videos'),
    buildTranslatedRoute(backendLocale, 'products'),
    buildTranslatedRoute(backendLocale, 'production'),
    buildTranslatedRoute(backendLocale, 'blog'),
    buildTranslatedRoute(backendLocale, 'events'),
    buildTranslatedRoute(backendLocale, 'career'),
    buildTranslatedRoute(backendLocale, 'contact')
  ])

  return [
    { label: 'home', href: '/' },
    {
      label: 'corporate',
      href: corporateHref,
      submenu: [
        ...corporatePages,
        { label: 'catalogues', href: cataloguesHref },
        { label: 'certificates', href: certificatesHref },
        { label: 'photo_galleries', href: photoGalleriesHref },
        { label: 'videos', href: videosHref }
      ]
    },
    {
      label: 'products',
      href: productsHref,
      submenu: productsSubmenu
    },
    { label: 'production', href: productionHref },
    { label: 'blog', href: blogHref },
    { label: 'events', href: eventsHref },
    { label: 'career', href: careerHref },
    { label: 'contact', href: contactHref }
  ]
}
